import { Link } from '@tanstack/react-router';
import { ArrowRight, Zap } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useMe } from '@/features/auth/hooks';
import { billingLinkVisible } from '@/features/billing/visibility';
import { useOnboardingState } from '@/features/dashboard/hooks';
import { formatServerDateShort } from '@/lib/dates';
import { useCompanyUsage } from '../hooks';

/** 3,253,345 -> "3.3M"; 48_200 -> "48.2K". Stable width via tabular-nums on
 * the pill, so the 30s refresh never makes the header jiggle. */
const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * TokenUsagePill — the company-wide token meter in the header.
 *
 * Muted on purpose: a signal that is always on stops signaling (the future
 * soft-limit feature is what earns amber/red). Renders nothing while loading,
 * on error, or when there is no usage this period — never a broken or zero
 * pill. Tokens only, never money; the "View billing" link is owner-only
 * because /billing 403s everyone else.
 */
export function TokenUsagePill() {
  const usage = useCompanyUsage();
  const me = useMe();
  const onboarding = useOnboardingState();

  if (!usage.data) return null;
  const { total_tokens, period_end, as_of } = usage.data;
  const showBilling = billingLinkVisible(me.data, onboarding);

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Company token usage this period"
              className="
                hidden sm:inline-flex items-center gap-1.5
                rounded-full border border-white/15 bg-white/5
                px-3 py-1.5
                text-[13px] font-semibold tracking-[0.02em] tabular-nums
                text-header-muted cursor-pointer
                transition-colors duration-150
                hover:text-white hover:border-white/35 hover:bg-white/10
                data-[state=open]:text-white data-[state=open]:border-white/35
                focus-visible:outline-2 focus-visible:outline-electric focus-visible:outline-offset-2
              "
            >
              <Zap className="size-3.5" aria-hidden="true" />
              {compact.format(total_tokens)}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Company token usage this period</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" className="w-64">
        <div className="text-[20px] font-bold text-navy tabular-nums leading-tight">
          {total_tokens.toLocaleString('en-US')}
          <span className="ml-1.5 text-[12px] font-medium text-muted">tokens</span>
        </div>
        <div className="mt-1 text-[12.5px] text-muted">
          This billing period &middot; resets {formatServerDateShort(period_end)}
        </div>
        <div className="mt-0.5 text-[11.5px] text-muted opacity-75">
          as of {formatServerDateShort(as_of)}
        </div>
        {showBilling && (
          <Link
            to="/billing"
            className="mt-3 flex items-center gap-1.5 border-t border-line pt-2.5 text-[13px] font-semibold text-brand-blue hover:underline"
          >
            View billing
            <ArrowRight className="size-3.5" aria-hidden="true" />
          </Link>
        )}
      </PopoverContent>
    </Popover>
  );
}
